/**
 * Main directional spectrum estimation function
 *
 * This is an Augment-assisted port of wave_signal_processing/spectral_analysis.py from sofarocean/spotter-analysis-examples.git
 */
// Custom Levenberg-Marquardt implementation for root finding
import { LevenbergMarquardtResult, levenbergMarquardt } from '@/utils/estimateDirectionalSpectrum/levenberg-marquardt';

import { DirectionalSpectrumInput, DirectionalSpectrumOutput, EstimationOptions } from './types';

const RAD_PER_DEG = Math.PI / 180;

/**
 * Calculate direction increments for integration
 */
export function calculateDirectionIncrements(directionsRadians: number[]): number[] {
    const increments = new Array<number>(directionsRadians.length);

    for (let i = 0; i < directionsRadians.length; i++) {
        // Forward difference with wraparound
        const nextIdx = (i + 1) % directionsRadians.length;
        let forwardDiff = directionsRadians[nextIdx]! - directionsRadians[i]!;

        // Handle wraparound
        if (forwardDiff > Math.PI) {
            forwardDiff -= 2 * Math.PI;
        } else if (forwardDiff < -Math.PI) {
            forwardDiff += 2 * Math.PI;
        }

        // Backward difference with wraparound
        const prevIdx = (i - 1 + directionsRadians.length) % directionsRadians.length;
        let backwardDiff = directionsRadians[i]! - directionsRadians[prevIdx]!;

        // Handle wraparound
        if (backwardDiff > Math.PI) {
            backwardDiff -= 2 * Math.PI;
        } else if (backwardDiff < -Math.PI) {
            backwardDiff += 2 * Math.PI;
        }

        // Average of forward and backward differences
        increments[i] = (Math.abs(forwardDiff) + Math.abs(backwardDiff)) / 2;
    }

    return increments;
}

/**
 * Calculate twiddle factors (directional basis functions)
 */
function calculateTwiddleFactors(directionsRadians: number[]): number[][] {
    const nDirections = directionsRadians.length;
    const twiddleFactors: number[][] = [
        new Array<number>(nDirections), // cos(theta)
        new Array<number>(nDirections), // sin(theta)
        new Array<number>(nDirections), // cos(2*theta)
        new Array<number>(nDirections), // sin(2*theta)
    ];

    for (let i = 0; i < nDirections; i++) {
        const theta = (3 * Math.PI) / 2 - directionsRadians[i]!;
        twiddleFactors[0]![i] = Math.cos(theta);
        twiddleFactors[1]![i] = Math.sin(theta);
        twiddleFactors[2]![i] = Math.cos(2 * theta);
        twiddleFactors[3]![i] = Math.sin(2 * theta);
    }

    return twiddleFactors;
}

/**
 * Calculate initial guess for Lagrange multipliers
 */
function calculateInitialGuess(a1: number, b1: number, a2: number, b2: number): number[] {
    const factor = 1 + a1 * a1 + b1 * b1 + a2 * a2 + b2 * b2;

    return [
        2 * a1 * a2 + 2 * b1 * b2 - 2 * a1 * factor,
        2 * a1 * b2 - 2 * b1 * a2 - 2 * b1 * factor,
        a1 * a1 - b1 * b1 - 2 * a2 * factor,
        2 * a1 * b1 - 2 * b2 * factor,
    ];
}

/**
 * Maximum entropy directional distribution
 */
function calculateDirectionalDistribution(
    lambdas: number[],
    twiddleFactors: number[][],
    directionIncrements: number[]
): number[] {
    const nDirections = twiddleFactors[0]!.length;
    const innerProduct = new Array<number>(nDirections);

    // Calculate inner product
    for (let i = 0; i < nDirections; i++) {
        innerProduct[i] = 0;
        for (let j = 0; j < 4; j++) {
            const lambda = lambdas[j];
            const twiddleFactor = twiddleFactors[j]?.[i];
            if (lambda !== undefined && twiddleFactor !== undefined) {
                const currentValue = innerProduct[i];
                if (currentValue !== undefined) {
                    innerProduct[i] = currentValue + lambda * twiddleFactor;
                }
            }
        }
    }

    // Subtract minimum to avoid numerical issues
    const minValue = Math.min(...innerProduct);
    for (let i = 0; i < nDirections; i++) {
        const currentValue = innerProduct[i];
        if (currentValue !== undefined) {
            innerProduct[i] = currentValue - minValue;
        }
    }

    // Calculate exponential and normalization
    const expValues = innerProduct.map((x) => Math.exp(-x));
    const normalization = expValues.reduce((sum, val, i) => sum + val * directionIncrements[i]!, 0);

    // Return normalized distribution
    return expValues.map((val) => val / normalization);
}

/**
 * Moment constraint function for root finding
 */
function momentConstraints(
    lambdas: number[],
    twiddleFactors: number[][],
    moments: number[],
    directionIncrements: number[]
): number[] {
    const distribution = calculateDirectionalDistribution(lambdas, twiddleFactors, directionIncrements);
    const constraints = new Array<number>(4);

    for (let m = 0; m < 4; m++) {
        let sum = 0;
        for (let i = 0; i < distribution.length; i++) {
            sum += twiddleFactors[m]![i]! * distribution[i]! * directionIncrements[i]!;
        }
        constraints[m] = moments[m]! - sum;
    }

    return constraints;
}

/**
 * Levenberg-Marquardt root finder using ml-levenberg-marquardt package
 */
function findRoots(
    initialGuess: number[],
    twiddleFactors: number[][],
    moments: number[],
    directionIncrements: number[],
    options: Required<EstimationOptions>
): LevenbergMarquardtResult {
    const { maxIterations, tolerance } = options;

    // Define the residual function for the Levenberg-Marquardt solver
    const residualFunction = (lambdas: number[]): number[] => {
        return momentConstraints(lambdas, twiddleFactors, moments, directionIncrements);
    };

    // Use ml-levenberg-marquardt package adapted for root finding
    // We create a "curve fitting" problem where we want to fit residuals to zero
    const data = {
        x: [0, 1, 2, 3], // Dummy x values for the 4 constraints
        y: [0, 0, 0, 0], // Target is zero residuals for all constraints
    };

    // Create a parameterized function that returns residuals
    const parameterizedFunction = (params: number[]) => {
        const residuals = residualFunction(params);
        // Return a function that maps constraint index to residual value
        return (constraintIndex: number) => residuals[constraintIndex] || 0;
    };

    return levenbergMarquardt(data, parameterizedFunction, {
        initialValues: initialGuess,
        maxIterations,
        errorTolerance: tolerance, // Match Python tolerance
        gradientDifference: 1e-8, // Finite difference step
        damping: 0.01, // Initial damping parameter
        dampingStepDown: 9, // Damping decrease factor
        dampingStepUp: 11, // Damping increase factor
        centralDifference: true, // Use central differences for better accuracy
    });
}

/**
 * Estimate directional spectrum from variance density and directional moments
 */
export function estimateDirectionalSpectrum(
    input: DirectionalSpectrumInput,
    options: EstimationOptions = {}
): DirectionalSpectrumOutput {
    const opts: Required<EstimationOptions> = {
        maxIterations: options.maxIterations ?? 500, // 100 x (x0.length) [which is 4]
        tolerance: options.tolerance ?? 1.49012e-8,
        validateInput: options.validateInput ?? true,
    };

    const { varianceDensity, a1, a2, b1, b2, directions } = input;

    // Convert directions to radians
    const directionsRadians = directions.map((d) => d * RAD_PER_DEG);

    // Calculate direction increments and twiddle factors
    const directionIncrements = calculateDirectionIncrements(directionsRadians);
    const twiddleFactors = calculateTwiddleFactors(directionsRadians);

    // Initialize output spectrum
    const nFrequencies = varianceDensity.length;
    const nDirections = directions.length;
    const spectrum: number[][] = Array(nFrequencies)
        .fill(null)
        .map(() => Array(nDirections).fill(0));

    // Process each frequency
    for (let freqIdx = 0; freqIdx < nFrequencies; freqIdx++) {
        const ezz = varianceDensity[freqIdx]!;

        // Skip zero energy frequencies
        if (ezz === 0) {
            continue;
        }

        const moments = [a1[freqIdx]!, b1[freqIdx]!, a2[freqIdx]!, b2[freqIdx]!];

        // Skip if any moments are NaN
        if (moments.some(isNaN)) {
            continue;
        }

        // Calculate initial guess for Lagrange multipliers
        const initialGuess = calculateInitialGuess(moments[0]!, moments[1]!, moments[2]!, moments[3]!);

        // Find optimal Lagrange multipliers
        const { parameterError, parameterValues } = findRoots(
            initialGuess,
            twiddleFactors,
            moments,
            directionIncrements,
            opts
        );

        if (parameterError > opts.tolerance) {
            console.error('Estimated direction spectrum failed to converge Lagrange multipliers', {
                freqIdx,
                ezz,
                moments,
            });
        }

        // Calculate directional distribution
        const distribution = calculateDirectionalDistribution(parameterValues, twiddleFactors, directionIncrements);

        // Scale by variance density and convert 1/rad to 1/degrees
        for (let dirIdx = 0; dirIdx < nDirections; dirIdx++) {
            spectrum[freqIdx]![dirIdx] = distribution[dirIdx]! * RAD_PER_DEG * ezz;
        }
    }

    // Calculate metadata
    let totalEnergy = 0;
    let maxValue = 0;
    let peakFrequencyIndex = 0;
    let peakDirectionIndex = 0;

    for (let freqIdx = 0; freqIdx < nFrequencies; freqIdx++) {
        for (let dirIdx = 0; dirIdx < nDirections; dirIdx++) {
            const value = spectrum[freqIdx]![dirIdx]!;
            totalEnergy += value;

            if (value > maxValue) {
                maxValue = value;
                peakFrequencyIndex = freqIdx;
                peakDirectionIndex = dirIdx;
            }
        }
    }

    return {
        spectrum,
        directions,
        metadata: {
            totalEnergy,
            peakFrequencyIndex,
            peakDirectionIndex,
            maxValue,
        },
    };
}
